import { useState } from "react";
import { Plus } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import Reveal from "../components/Reveal";
import GridTexture from "../components/GridTexture";

const faqData = [
  {
    question: "Is de prijs maandelijks? Word ik opnieuw gefactureerd?",
    answer:
      "Nee, het is een eenmalige betaling. Wij rekenen geen maandelijkse kosten en verlengen je abonnement niet automatisch. Je betaalt één keer en krijgt toegang voor de gekozen periode.",
  },
  {
    question: "Hoe ontvang ik mijn inloggegevens na aankoop?",
    answer:
      "Na het afronden van je bestelling worden je inloggegevens (M3U-link, Xtream Codes-gebruikersnaam en wachtwoord) binnen enkele minuten naar je e-mailadres en via WhatsApp verzonden. Controleer ook je spammap.",
  },
  {
    question: "Kan ik IPTV gebruiken als ik in een appartement woon?",
    answer:
      "Ja, IPTV is perfect voor bewoners van appartementen. Er is geen schotelantenne of ingewikkelde installatie nodig. Je hebt alleen een internetverbinding en een compatibel apparaat nodig (Smart TV, TV Box, smartphone, tablet, computer, enz.).",
  },
  {
    question: "Op hoeveel apparaten kan ik tegelijkertijd kijken?",
    answer:
      "Dat hangt af van het gekozen pakket. De standaardpakketten ondersteunen 1 tot 2 gelijktijdige schermen. Onze Multi-Screen pakketten bieden 2, 3 of 4 gelijktijdige verbindingen.",
  },
  {
    question: "Waarom word ik doorgestuurd naar een andere site om af te rekenen?",
    answer:
      "Voor een veilige transactie word je doorgestuurd naar de betaalpagina van onze vertrouwde partner. Zodra je bestelling is afgerond, ontvang je je activeringsgegevens per e-mail en WhatsApp.",
  },
  {
    question: "Heb ik een schotelantenne nodig om TiviOne te gebruiken?",
    answer:
      "Nee, je maakt via internet verbinding met onze servers. Je hebt geen schotel of satellietapparatuur nodig — alleen een stabiele internetverbinding.",
  },
  {
    question: "Wat zijn de minimale vereisten voor de internetsnelheid?",
    answer:
      "Voor HD-kwaliteit raden we een stabiele internetverbinding van minstens 10 Mbps aan. Voor 4K Ultra HD is een snelheid van minstens 25 Mbps aanbevolen.",
  },
  {
    question: "Zijn er volwassenenkanalen inbegrepen en hoe beveilig ik ze?",
    answer:
      "Ja, optionele volwassenenkanalen en VOD zijn inbegrepen. Deze zijn standaard beveiligd met een pincode, zodat ze veilig verborgen blijven voor kinderen.",
  },
  {
    question: "Wat als ik hulp nodig heb bij de installatie?",
    answer:
      "Ons ondersteuningsteam is 24/7 beschikbaar via WhatsApp en e-mail om je stap voor stap te begeleiden bij de installatie op elk type apparaat.",
  },
  {
    question: "Bieden jullie een geld-terug-garantie?",
    answer:
      "Ja, we bieden een geld-terug-garantie van 15 dagen. Als je niet tevreden bent met onze service, kun je binnen 15 dagen een volledige terugbetaling aanvragen.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqData.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

export default function Faq() {
  const [open, setOpen] = useState({ 0: true });

  const toggle = (index) => {
    setOpen((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <section id="faq" className="py-14 sm:py-20 bg-surface-secondary relative overflow-hidden" style={{ "--section-bg": "#000000" }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <GridTexture />
      <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto relative">
        <SectionHeading
          kicker="Veelgestelde vragen"
          title={
            <>
              Alles wat je wilt{" "}
              <span className="text-transparent bg-clip-text bg-gradient-text">
                weten
              </span>
            </>
          }
          subtitle="Vind antwoorden op de meest gestelde vragen over TiviOne"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 max-w-full mx-auto">
          {faqData.map((item, index) => {
            const isOpen = !!open[index];
            return (
              <Reveal
                key={item.question}
                delay={(index % 4) * 60}
                className={`self-start rounded-xl sm:rounded-2xl bg-gradient-button p-[3px] transition-all duration-300 ${
                  isOpen ? "shadow-glow-cyan-sm" : "shadow-sm hover:shadow-md"
                }`}
              >
                <div
                  onClick={() => toggle(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggle(index);
                    }
                  }}
                  className="bg-surface-card rounded-[calc(0.75rem-3px)] sm:rounded-[calc(1rem-3px)] p-4 sm:p-6 cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-bold text-white text-sm sm:text-base lg:text-lg leading-tight">
                      {item.question}
                    </h3>
                    <div
                      className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isOpen
                          ? "bg-gradient-button rotate-45"
                          : "bg-white/5 border border-line"
                      }`}
                    >
                      <Plus
                        className={`w-4 h-4 transition-colors duration-300 ${
                          isOpen ? "text-white" : "text-accent"
                        }`}
                      />
                    </div>
                  </div>
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? "grid-rows-[1fr] opacity-100 mt-3 sm:mt-4" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="text-ink-secondary pt-3 sm:pt-4 border-t border-line leading-relaxed text-sm sm:text-base">
                        {item.answer}
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
